import { DOMParser, XMLSerializer } from "@xmldom/xmldom"
import { WebMercatorViewport } from "@deck.gl/core"

type Coordinate = [number, number]

export type Placemark = {
    type: string
    spots: Coordinate[]
    width?: number
    outer?: boolean
    inner?: boolean
    color?: string
    fill?: string
    size?: number
}

type Perimeter = {
    paths: Coordinate[][]
}

type CoordinateMapperFunction = (x: number, y: number) => Coordinate

type DrawFunction = (
    placemark: Placemark & { coordinateMapper: CoordinateMapperFunction }
) => HTMLElement

export const project = (lngLat: number[]) => new WebMercatorViewport().project(lngLat)

export const getBox = (perimeters: Perimeter[]) => {
    let maxWidth = -Infinity,
        maxHeight = -Infinity
    perimeters.forEach((perimeter) => {
        let minX = Infinity,
            maxX = -Infinity,
            minY = Infinity,
            maxY = -Infinity
        perimeter.paths.forEach((polygon: [number, number][]) => {
            polygon.forEach((coordiate) => {
                const [x, y] = project(coordiate)
                minX = Math.min(minX, x)
                maxX = Math.max(maxX, x)
                minY = Math.min(minY, y)
                maxY = Math.max(maxY, y)
            })
        })
        maxWidth = Math.max(maxWidth, maxX - minX)
        maxHeight = Math.max(maxHeight, maxY - minY)
    })
    return { maxWidth, maxHeight }
}

function getPointPlacemark(element: Element, allSpots: Coordinate[]) {
    const placemarks: Placemark[] = []
    const pointElements = element.getElementsByTagName("Point")
    for (let i = 0; i < pointElements.length; i++) {
        const coordinatesElement = pointElements[i].getElementsByTagName("coordinates")[0]
        if (!coordinatesElement?.textContent)
            throw new Error("Invalid point placemark, could not get coordinates")
        const [lng, lat] = project(
            coordinatesElement.textContent.split(",").map(parseFloat).slice(0, 2) as Coordinate
        )
        allSpots.push([lng, lat])
        placemarks.push({
            type: "Point",
            spots: [[lng, lat]],
        })
    }
    return placemarks
}

function getDashedLinePlacemark(element: Element, allSpots: Coordinate[]) {
    const placemarks: Placemark[] = []
    const linestringElements = element.getElementsByTagName("DashedLineString")
    for (let i = 0; i < linestringElements.length; i++) {
        const coordinatesElement = linestringElements[i].getElementsByTagName("coordinates")[0]
        if (!coordinatesElement?.textContent)
            throw new Error("Invalid linestring placemark, could not get coordinates")
        const placemark: Placemark = {
            type: "DashedLineString",
            spots: [],
        }
        const coordinates = coordinatesElement.textContent.replaceAll("\n", " ").split(" ")
        for (let j = 0; j < coordinates.length; j++) {
            let arr = coordinates[j].split(",").map(parseFloat) as Coordinate
            if (arr.length < 3) continue
            arr = project(arr) as Coordinate
            allSpots.push(arr)
            placemark.spots.push(arr)
        }
        placemarks.push(placemark)
    }
    return placemarks
}

function getLinePlacemark(element: Element, allSpots: Coordinate[]) {
    const placemarks: Placemark[] = []
    const linestringElements = element.getElementsByTagName("LineString")
    for (let i = 0; i < linestringElements.length; i++) {
        const coordinatesElement = linestringElements[i].getElementsByTagName("coordinates")[0]
        if (!coordinatesElement?.textContent)
            throw new Error("Invalid linestring placemark, could not get coordinates")
        const placemark: Placemark = {
            type: "LineString",
            spots: [],
        }
        const coordinates = coordinatesElement.textContent.replaceAll("\n", " ").split(" ")
        for (let j = 0; j < coordinates.length; j++) {
            let arr = coordinates[j].split(",").map(parseFloat) as Coordinate
            if (arr.length < 3) continue
            arr = project(arr) as Coordinate
            allSpots.push(arr)
            placemark.spots.push(arr)
        }
        placemarks.push(placemark)
    }
    return placemarks
}

function getPolygonPlacemark(element: Element, allSpots: Coordinate[]) {
    const placemarks: Placemark[] = []
    const polygonElements = element.getElementsByTagName("Polygon")
    for (let i = 0; i < polygonElements.length; i++) {
        const polygonElement = polygonElements[i]
        const linearRingElements = Array.from(polygonElement.getElementsByTagName("LinearRing"))
        const isDonut =
            linearRingElements.find((e) => e.parentNode?.nodeName === "outerBoundaryIs") &&
            linearRingElements.find((e) => e.parentNode?.nodeName === "innerBoundaryIs")
        for (let j = 0; j < linearRingElements.length; j++) {
            const linearRingElement = linearRingElements[j]
            const placemark: Placemark = {
                type: "Polygon",
                spots: [],
            }
            if (linearRingElement.parentNode?.nodeName === "outerBoundaryIs" && isDonut)
                placemark.outer = true
            else if (linearRingElement.parentNode?.nodeName === "innerBoundaryIs" && isDonut)
                placemark.inner = true
            const coordinatesElement = linearRingElement.getElementsByTagName("coordinates")[0]
            if (!coordinatesElement?.textContent)
                throw new Error("Invalid polygon placemark, could not get coordinates")
            const coordinates = coordinatesElement.textContent.replaceAll("\n", " ").split(" ")
            for (let k = 0; k < coordinates.length; k++) {
                let arr = coordinates[k].split(",").map(parseFloat) as Coordinate
                if (arr.length < 3) continue
                arr = project(arr.slice(0, 2)) as Coordinate
                allSpots.push(arr)
                placemark.spots.push(arr)
            }
            placemarks.push(placemark)
        }
    }
    return placemarks
}

const createSVGElement = (type: string) => {
    const svgDoc = new DOMParser().parseFromString(
        '<svg xmlns="http://www.w3.org/2000/svg"></svg>',
        "text/xml"
    )
    return svgDoc.createElement(type)
}

const drawPoint: DrawFunction = ({ spots, coordinateMapper, size }) => {
    const circle = createSVGElement("circle")
    const [x, y] = coordinateMapper(...spots[0])
    circle.setAttribute("cx", x.toString())
    circle.setAttribute("cy", y.toString())
    circle.setAttribute("r", size?.toString() || "3")
    circle.setAttribute("fill", "#F15D4FFF")
    return circle
}

const drawLine: DrawFunction = ({ spots, width, coordinateMapper }) => {
    const polyline = createSVGElement("polyline")
    polyline.setAttribute("points", mapCoordinatesToSVGString(coordinateMapper, spots))
    polyline.setAttribute("fill", "none")
    polyline.setAttribute("stroke", "white")
    polyline.setAttribute("stroke-width", width?.toString() || "2")
    return polyline
}

const drawDashedLine: DrawFunction = ({ spots, width, coordinateMapper }) => {
    const polyline = createSVGElement("polyline")
    polyline.setAttribute("points", mapCoordinatesToSVGString(coordinateMapper, spots))
    polyline.setAttribute("fill", "none")
    polyline.setAttribute("stroke", "white")
    polyline.setAttribute("stroke-width", width?.toString() || "2")
    polyline.setAttribute("stroke-dasharray", "3 3")
    return polyline
}

const drawPolygon: DrawFunction = ({
    spots,
    width,
    color,
    fill,
    outer,
    inner,
    coordinateMapper,
}) => {
    const polygon = createSVGElement("polygon")
    polygon.setAttribute("points", mapCoordinatesToSVGString(coordinateMapper, spots))
    polygon.setAttribute("fill", fill || outer ? "#f15d4f40" : inner ? "white" : "none")
    polygon.setAttribute("stroke", color || "red")
    polygon.setAttribute("stroke-width", width?.toString() || "2")
    return polygon
}

const getPlaceMarks = (kml: string, placemarks: Placemark[], allSpots: Coordinate[]) => {
    const kmlDoc = new DOMParser().parseFromString(kml, "text/xml")
    const placemarkElements = Array.from(kmlDoc.getElementsByTagName("Placemark"))
    if (placemarkElements.length === 0)
        throw new Error(
            "No placemarks found in kml, data is expected to be inside of placemark elements"
        )
    for (const placemarkElement of placemarkElements) {
        for (const type of Object.keys(placemarkTypeMap)) {
            if (placemarkElement.getElementsByTagName(type).length > 0)
                placemarks.push(...placemarkTypeMap[type].get(placemarkElement, allSpots))
        }
    }
}

const placemarkTypeMap: Record<
    string,
    {
        get: (element: Element, allSpots: Coordinate[]) => Placemark[]
        draw: DrawFunction
    }
> = {
    Point: {
        get: getPointPlacemark,
        draw: drawPoint,
    },
    DashedLineString: {
        get: getDashedLinePlacemark,
        draw: drawDashedLine,
    },
    LineString: {
        get: getLinePlacemark,
        draw: drawLine,
    },
    Polygon: { get: getPolygonPlacemark, draw: drawPolygon },
}

const getCoordinateMapperFunction = (
    allSpots: Coordinate[],
    box: { width: number; height: number },
    margin: number,
    svgWidth: number,
    svgHeight: number
) => {
    const [maxX, maxY, minX, minY] = [
        Math.max(...allSpots.map((spot) => spot[0])),
        Math.max(...allSpots.map((spot) => spot[1])),
        Math.min(...allSpots.map((spot) => spot[0])),
        Math.min(...allSpots.map((spot) => spot[1])),
    ]

    let { width, height } = box
    if (!width || !height) {
        width = maxX - minX
        height = maxY - minY
    }
    const aspectRatio = width / height
    const svgAspectRatio = (svgWidth - margin * 2) / (svgHeight - margin * 2)

    const scale =
        aspectRatio > svgAspectRatio
            ? (svgWidth - margin * 2) / width
            : (svgHeight - margin * 2) / height

    return (x: number, y: number): Coordinate => {
        const cx = (x - minX) * scale + margin + (svgWidth - margin * 2 - (maxX - minX) * scale) / 2
        const cy =
            (y - minY) * scale + margin + (svgHeight - margin * 2 - (maxY - minY) * scale) / 2
        return [cx, cy]
    }
}

const mapCoordinatesToSVGString = (mapper: CoordinateMapperFunction, points: Coordinate[]) => {
    return points.reduce((acc, [lng, lat]) => {
        const [cx, cy] = mapper(lng, lat)
        return acc + `${cx} ${cy},`
    }, "")
}

export function createSVGFromKML(
    kmls: string | string[],
    svgWidth: number,
    svgHeight: number,
    margin: number,
    lineWidth: number,
    color: string | string[],
    box: { width: number; height: number } = { width: 0, height: 0 },
    options: { fill?: string; size?: number } = {}
) {
    const placemarks: Placemark[] = []
    const allSpots: Coordinate[] = []
    const svgDoc = new DOMParser().parseFromString(
        '<svg xmlns="http://www.w3.org/2000/svg"></svg>',
        "text/xml"
    )

    if (typeof kmls === "string") getPlaceMarks(kmls, placemarks, allSpots)
    else kmls.forEach((kml) => getPlaceMarks(kml, placemarks, allSpots))

    svgDoc.documentElement.setAttribute("width", svgWidth.toString())
    svgDoc.documentElement.setAttribute("height", svgHeight.toString())
    svgDoc.documentElement.setAttribute("viewbox", `0 0 ${svgWidth} ${svgHeight}`)

    const coordinateMapper = getCoordinateMapperFunction(allSpots, box, margin, svgWidth, svgHeight)
    placemarks.forEach((placemark, i) => {
        let drawColor = ""
        if (typeof color === "string") {
            drawColor = color
        } else {
            drawColor = color[i]
        }
        svgDoc.documentElement.appendChild(
            placemarkTypeMap[placemark.type].draw({
                color: drawColor,
                fill: options.fill,
                size: options.size,
                width: lineWidth,
                coordinateMapper,
                ...placemark,
            })
        )
    })

    return new XMLSerializer().serializeToString(svgDoc)
}
